import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Componente ErrorBoundary para capturar erros na árvore de componentes.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    // Atualiza o estado para que a próxima renderização mostre a UI de fallback.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log do erro (poderia ser enviado para um serviço de monitoramento)
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      // UI de fallback quando ocorre um erro
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 font-sans">
          <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-xl border border-red-100 dark:border-red-900/30 max-w-lg w-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                <AlertTriangle size={32} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">Modo Diagnóstico</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Erro crítico detectado na aplicação.</p>
              </div>
            </div>
            
            <div className="bg-slate-900 text-slate-300 p-4 rounded-lg text-xs font-mono overflow-auto mb-6 max-h-48 border border-slate-700 shadow-inner">
              <p className="font-bold text-red-400 mb-2">Error Message:</p>
              {this.state.error?.toString() || 'Erro desconhecido'}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 p-4 rounded-lg text-sm mb-6 border border-blue-100 dark:border-blue-800">
              <strong>Instrução:</strong> Abra o console do desenvolvedor (F12) para visualizar a stack trace completa e detalhes da rede.
            </div>
            
            <button 
              type="button"
              onClick={() => window.location.reload()}
              className="w-full bg-slate-800 text-white py-3 rounded-lg font-bold hover:bg-slate-900 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-300/50 dark:shadow-none"
            >
              <RefreshCw size={18} /> Tentar Recarregar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;